/**
 * Key management functions for Kript
 */
import * as openpgp from 'openpgp';
import { PGPError, ErrorCode, } from './types.js';
import { getShortKeyId, withSecurePassphrase, secureClear } from './utils.js';
import { uint8ArrayToBase64 } from './crypto.js';
/**
 * Map our algorithm names to OpenPGP.js config
 */
function getAlgorithmConfig(algorithm) {
    switch (algorithm) {
        case 'rsa2048':
            return { type: 'rsa', rsaBits: 2048 };
        case 'rsa4096':
            return { type: 'rsa', rsaBits: 4096 };
        case 'ecc':
        case 'curve25519':
            return { type: 'ecc', curve: 'curve25519' };
        default:
            return { type: 'ecc', curve: 'curve25519' };
    }
}
/**
 * Extract key usage flags from an OpenPGP key
 */
function extractKeyUsage(key) {
    // OpenPGP.js doesn't expose flags directly, so we check capabilities
    let canEncrypt = false;
    try {
        canEncrypt = key.getEncryptionKey(undefined, undefined, undefined) !== undefined;
    }
    catch {
        canEncrypt = false;
    }
    return {
        certify: true, // Primary keys can always certify
        sign: true,
        encrypt: canEncrypt,
        authenticate: false,
    };
}
/**
 * Extract key info from an OpenPGP key
 */
export async function extractKeyInfo(key) {
    const keyPacket = key.keyPacket;
    const fingerprint = key.getFingerprint().toUpperCase();
    const keyId = getShortKeyId(fingerprint);
    // Get user IDs
    const userIds = key.users.map((user) => {
        const userId = user.userID;
        return {
            name: userId?.name ?? '',
            email: userId?.email ?? '',
            comment: userId?.comment || undefined,
        };
    });
    // Get algorithm info using numeric algorithm enum
    let algorithm = 'Unknown';
    let bitLength;
    let curve;
    const algoNum = keyPacket.algorithm;
    const publicParams = keyPacket.publicParams;
    // RSA algorithms: 1 = rsaEncryptSign, 2 = rsaEncrypt, 3 = rsaSign
    if (algoNum === 1 || algoNum === 2 || algoNum === 3) {
        if (publicParams?.n) {
            bitLength = publicParams.n.length * 8;
        }
        algorithm = bitLength ? `RSA-${bitLength}` : 'RSA';
    }
    else if (algoNum >= 18) {
        // ECC algorithms: 18 = ecdh, 19 = ecdsa, 22 = eddsa/ed25519Legacy, 25 = x25519, 27 = ed25519
        curve = 'curve25519';
        algorithm = 'ECC (Curve25519)';
    }
    // Get expiration
    const expirationTime = await key.getExpirationTime();
    const expiration = expirationTime && expirationTime !== Infinity ? new Date(expirationTime) : undefined;
    // Check revocation
    const revoked = await key.isRevoked();
    // Get subkeys
    const subkeys = await Promise.all(key.subkeys.map(async (subkey) => {
        const subFingerprint = subkey.getFingerprint().toUpperCase();
        const subKeyPacket = subkey.keyPacket;
        let subAlgorithm = 'Unknown';
        let subBitLength;
        let subCurve;
        const subAlgoNum = subKeyPacket.algorithm;
        const subPublicParams = subKeyPacket.publicParams;
        // RSA algorithms: 1 = rsaEncryptSign, 2 = rsaEncrypt, 3 = rsaSign
        if (subAlgoNum === 1 || subAlgoNum === 2 || subAlgoNum === 3) {
            if (subPublicParams?.n) {
                subBitLength = subPublicParams.n.length * 8;
            }
            subAlgorithm = subBitLength ? `RSA-${subBitLength}` : 'RSA';
        }
        else if (subAlgoNum >= 18) {
            // ECC algorithms
            subCurve = 'curve25519';
            subAlgorithm = 'ECC (Curve25519)';
        }
        const subExpiration = await subkey.getExpirationTime();
        return {
            keyId: getShortKeyId(subFingerprint),
            fingerprint: subFingerprint,
            algorithm: subAlgorithm,
            bitLength: subBitLength,
            curve: subCurve,
            creationTime: subKeyPacket.created,
            expirationTime: subExpiration && subExpiration !== Infinity ? new Date(subExpiration) : undefined,
            usage: {
                certify: false,
                sign: false,
                encrypt: true,
                authenticate: false,
            },
            revoked: false, // Subkey revocation check simplified
        };
    }));
    return {
        keyId,
        fingerprint,
        algorithm,
        bitLength,
        curve,
        creationTime: keyPacket.created,
        expirationTime: expiration,
        userIds,
        usage: extractKeyUsage(key),
        revoked,
        isPrivate: key.isPrivate(),
        subkeys,
    };
}
/**
 * Generate a new PGP key pair
 */
export async function generateKeyPair(options) {
    const { algorithm, userIds, passphrase, expirationTime } = options;
    const algoConfig = getAlgorithmConfig(algorithm);
    // Save original config for restoration
    const originalComment = openpgp.config.commentString;
    const originalShowComment = openpgp.config.showComment;
    try {
        // Set Kript branding in armor headers
        openpgp.config.commentString = 'Generated with Kript.xyz';
        openpgp.config.showComment = true;
        const { privateKey, publicKey, revocationCertificate } = await openpgp.generateKey({
            type: algoConfig.type,
            rsaBits: algoConfig.rsaBits,
            curve: algoConfig.curve,
            userIDs: userIds.map((uid) => ({
                name: uid.name,
                email: uid.email,
                comment: uid.comment,
            })),
            passphrase: passphrase || undefined,
            keyExpirationTime: expirationTime,
            format: 'armored',
        });
        // Parse the generated key to extract info
        const parsedKey = await openpgp.readKey({ armoredKey: privateKey });
        const keyInfo = await extractKeyInfo(parsedKey);
        return {
            publicKey,
            privateKey,
            revocationCertificate,
            keyInfo,
        };
    }
    catch (error) {
        throw new PGPError(ErrorCode.ENCRYPTION_FAILED, `Failed to generate key pair: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
    }
    finally {
        // Always restore original config
        openpgp.config.commentString = originalComment;
        openpgp.config.showComment = originalShowComment;
    }
}
/**
 * Read and parse an armored key
 */
export async function readKey(armoredKey) {
    try {
        // Try reading as public key first
        const key = await openpgp.readKey({ armoredKey });
        return key;
    }
    catch {
        // Try as private key
        try {
            const privateKey = await openpgp.readPrivateKey({ armoredKey });
            return privateKey;
        }
        catch (error) {
            throw new PGPError(ErrorCode.INVALID_KEY, `Failed to parse key: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
        }
    }
}
/**
 * Read and parse a binary key (Uint8Array)
 */
export async function readKeyBinary(binaryKey) {
    try {
        // Try reading as public key first
        const key = await openpgp.readKey({ binaryKey });
        return key;
    }
    catch {
        // Try as private key
        try {
            const privateKey = await openpgp.readPrivateKey({ binaryKey });
            return privateKey;
        }
        catch (error) {
            throw new PGPError(ErrorCode.INVALID_KEY, `Failed to parse binary key: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
        }
    }
}
/**
 * Convert a binary key to armored format
 */
export async function binaryKeyToArmored(binaryKey) {
    const key = await readKeyBinary(binaryKey);
    return key.armor();
}
/**
 * Read multiple keys from armored text
 */
export async function readKeys(armoredKeys) {
    try {
        const keys = await openpgp.readKeys({ armoredKeys });
        return keys;
    }
    catch {
        // Try reading as single key
        try {
            const key = await readKey(armoredKeys);
            return [key];
        }
        catch (error) {
            throw new PGPError(ErrorCode.INVALID_KEY, `Failed to parse keys: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
        }
    }
}
/**
 * Import keys from armored text
 */
export async function importKeys(armoredKeys) {
    const result = {
        keys: [],
        errors: [],
    };
    try {
        const keys = await readKeys(armoredKeys);
        for (const key of keys) {
            try {
                const keyInfo = await extractKeyInfo(key);
                result.keys.push(keyInfo);
            }
            catch (error) {
                result.errors.push(`Failed to extract info from key: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    }
    catch (error) {
        result.errors.push(`Failed to parse keys: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    return result;
}
/**
 * Export a key in armored or binary format
 */
export async function exportKey(key, options = {}) {
    const { includePrivate = false, passphrase, armor = true } = options;
    try {
        if (includePrivate && key.isPrivate()) {
            if (passphrase) {
                // Decrypt with passphrase first if needed, then re-encrypt
                let decryptedKey = key;
                if (!key.isDecrypted()) {
                    decryptedKey = await openpgp.decryptKey({
                        privateKey: key,
                        passphrase,
                    });
                }
                const encrypted = await openpgp.encryptKey({
                    privateKey: decryptedKey,
                    passphrase,
                });
                return armor ? encrypted.armor() : uint8ArrayToBase64(encrypted.write());
            }
            return armor ? key.armor() : uint8ArrayToBase64(key.write());
        }
        // Export public key only
        const publicKey = key.toPublic();
        return armor ? publicKey.armor() : uint8ArrayToBase64(publicKey.write());
    }
    catch (error) {
        throw new PGPError(ErrorCode.INVALID_KEY, `Failed to export key: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
    }
}
/**
 * Export a key as binary (Uint8Array)
 */
export async function exportKeyBinary(key, options = {}) {
    const { includePrivate = false, passphrase } = options;
    try {
        if (includePrivate && key.isPrivate()) {
            if (passphrase) {
                let decryptedKey = key;
                if (!key.isDecrypted()) {
                    decryptedKey = await openpgp.decryptKey({
                        privateKey: key,
                        passphrase,
                    });
                }
                const encrypted = await openpgp.encryptKey({
                    privateKey: decryptedKey,
                    passphrase,
                });
                return encrypted.write();
            }
            return key.write();
        }
        // Export public key only
        return key.toPublic().write();
    }
    catch (error) {
        throw new PGPError(ErrorCode.INVALID_KEY, `Failed to export key: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
    }
}
/**
 * Decrypt a private key with passphrase
 * Uses secure passphrase handling to clear the passphrase from memory after use
 */
export async function decryptPrivateKey(armoredKey, passphrase) {
    return withSecurePassphrase(passphrase, async (securePass) => {
        try {
            const key = await openpgp.readPrivateKey({ armoredKey });
            const decrypted = await openpgp.decryptKey({
                privateKey: key,
                passphrase: securePass,
            });
            return decrypted;
        }
        catch (error) {
            if (error instanceof Error && error.message.includes('passphrase')) {
                throw new PGPError(ErrorCode.INVALID_PASSPHRASE, 'Incorrect passphrase');
            }
            throw new PGPError(ErrorCode.INVALID_KEY, `Failed to decrypt key: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
        }
    });
}
/**
 * Change the passphrase of a private key
 * Uses secure passphrase handling to clear passphrases from memory after use
 */
export async function changePassphrase(armoredKey, oldPassphrase, newPassphrase) {
    try {
        // decryptPrivateKey already handles oldPassphrase securely
        const decrypted = await decryptPrivateKey(armoredKey, oldPassphrase);
        // Use withSecurePassphrase for the new passphrase
        return await withSecurePassphrase(newPassphrase, async (secureNewPass) => {
            const encrypted = await openpgp.encryptKey({
                privateKey: decrypted,
                passphrase: secureNewPass,
            });
            return encrypted.armor();
        });
    }
    catch (error) {
        if (error instanceof PGPError) {
            throw error;
        }
        throw new PGPError(ErrorCode.INVALID_KEY, `Failed to change passphrase: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
    }
    finally {
        // Attempt to clear original passphrase strings
        secureClear(oldPassphrase);
        secureClear(newPassphrase);
    }
}
/**
 * Generate a revocation certificate for a key
 * Uses secure passphrase handling to clear the passphrase from memory after use
 */
export async function generateRevocationCertificate(armoredPrivateKey, passphrase, reason) {
    const doRevoke = async (securePass) => {
        try {
            let privateKey = await openpgp.readPrivateKey({ armoredKey: armoredPrivateKey });
            if (!privateKey.isDecrypted() && securePass) {
                privateKey = await openpgp.decryptKey({ privateKey, passphrase: securePass });
            }
            const { publicKey: revokedKey } = await openpgp.revokeKey({
                key: privateKey,
                reasonForRevocation: {
                    flag: openpgp.enums.reasonForRevocation.noReason,
                    string: reason || 'Key revoked',
                },
            });
            // revokeKey returns a string in format: 'armored'
            return revokedKey;
        }
        catch (error) {
            throw new PGPError(ErrorCode.INVALID_KEY, `Failed to generate revocation certificate: ${error instanceof Error ? error.message : 'Unknown error'}`, error instanceof Error ? error : undefined);
        }
    };
    if (passphrase) {
        return withSecurePassphrase(passphrase, doRevoke);
    }
    return doRevoke();
}
/**
 * Check if a key is valid (not expired, not revoked)
 */
export async function validateKey(key) {
    const errors = [];
    // Check revocation
    if (await key.isRevoked()) {
        errors.push('Key has been revoked');
    }
    // Check expiration
    const expiration = await key.getExpirationTime();
    if (expiration && expiration !== Infinity && new Date(expiration) < new Date()) {
        errors.push('Key has expired');
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * Get the primary user ID from a key
 */
export function getPrimaryUserId(key) {
    const primaryUser = key.users[0];
    if (!primaryUser?.userID) {
        return null;
    }
    return {
        name: primaryUser.userID.name ?? '',
        email: primaryUser.userID.email ?? '',
        comment: primaryUser.userID.comment || undefined,
    };
}
//# sourceMappingURL=keys.js.map